import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dimensions } from '@opentok/client';
import { ShotsContext } from '../contexts/shots/provider';
import type { Shot, ShotFeed } from '../stores/types/shots';
import { GreenroomState } from '../stores/types/types';
import { LAYOUTS_BY_SIZE, VideoLayouts } from '../utils/video-layout';
import { OpenTokContext } from '../contexts/opentok/opentok-context';
import { FeedType } from '../components/general-components/dynamic-component';
import {
	getProfileFromStreamName,
	isStreamScreenshare,
	parseStreamConnectionData,
} from '../utils/utils';
import { createIsStreamNeeded, NeededFeeds } from '../utils/stream-needs';
import { selectFeatureFlag } from '../stores/selectors/blp-feature-flags';
import { FeatureFlagsEnum } from '../types/blp-feature-flags';

const MAX_VISIBLE_FEEDS = 10;

const RESOLUTIONS_1080 = {
	HIGH: { height: 1080, width: 1920 },
	MEDIUM: { height: 540, width: 960 }, // 960x540
	LOW: { height: 270, width: 480 },
};

const RESOLUTIONS_720 = {
	HIGH: { height: 720, width: 1280 },
	MEDIUM: { height: 360, width: 640 },
	LOW: { height: 180, width: 320 },
};

enum RESOLUTIONS_ENUM {
	HIGH='HIGH',
	MEDIUM='MEDIUM',
	LOW='LOW',
}

const useResolutionUpdate = () => {
	const { shots } = useSelector((state: GreenroomState) => state.ShotsReducer);
	const { feedScaling } = useSelector((state: GreenroomState) => state.SettingsReducer);
	const useFeedOptimization = !!useSelector(
		selectFeatureFlag(FeatureFlagsEnum.use_feed_optimization),
	);
	const { opentok } = useContext(OpenTokContext);
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const companionMode = useSelector((state: GreenroomState) => state.SessionReducer.companionMode);
	const connectedUsers = useSelector((state: GreenroomState) => state.SessionReducer.connectedUsers);
	const {
		connectedPresenterVideos,
		onsiteLocationStreams,
	} = useSelector((state: GreenroomState) => state.VideoReducer);

	const [
		preferredResolutions,
		setPreferredResolutions,
	] =	useState<{ [key: string]: Dimensions }>({});

	const {
		currentShot: {
			shot_data: {
				order, shotFeeds, forcedLayout,
			},
		},
	} = useContext(ShotsContext);

	const determineResolution = (
		shotLayout: string,
		position: any,
		presenterProfile: ShotFeed,
		layoutSize: number |undefined,
	) => {
		// position can be either string or number
		const stringifiedPosition = position.toString();
		let thisShotShouldBe1080;
		switch (shotLayout) {
			case 'single cover':
			case 'single-padding cover':
			case 'picture-in-picture bottom left':
				thisShotShouldBe1080 = stringifiedPosition === '0';
				break;
			case 'double cover':
			case 'picture-in-picture bottom right':
			case 'picture-in-picture top left':
				thisShotShouldBe1080 = stringifiedPosition === '1';
				break;
			case 'vertical two cover':
				thisShotShouldBe1080 = true;
				break;
			default:
				thisShotShouldBe1080 = false;
		}
		if (thisShotShouldBe1080) {
			return RESOLUTIONS_ENUM.HIGH;// { height: 1080, width: 1920 };
		} if (presenterProfile.audioOnly) {
			return RESOLUTIONS_ENUM.LOW;// { height: 180, width: 320 };
		} if (layoutSize && layoutSize < 5) {
			return RESOLUTIONS_ENUM.MEDIUM;// { height: 720, width: 1280 };
		}
		return RESOLUTIONS_ENUM.LOW;// { height: 180, width: 320 }
	};

	const getStreamIdFromGrUser = (grUser: number) => connectedPresenterVideos.find(
		({ profile, isScreenShare }) => profile === grUser && !isScreenShare,
	)?.id ?? '';

	const preferredResolutionChanged = (key: string, newPreferredResolution: Dimensions) => {
		if (!preferredResolutions[key]) {
			return true;
		}
		return preferredResolutions[key].width !== newPreferredResolution.width
			|| preferredResolutions[key].height !== newPreferredResolution.height;
	};

	const getResolutionsForShotFeeds = (
		currentShotFeeds: ShotFeed[],
		currentFeed: ShotFeed,
		currentForcedLayout: string,
	) => {
		const layoutSize = LAYOUTS_BY_SIZE.find(
			(config: VideoLayouts) => config.layouts.includes(currentForcedLayout ?? ''),
		)?.presenters;
		const type = currentFeed.feedType;
		const inCurrentShotIndexPres = (
			currentShotFeeds.findIndex(
				(feed) => {
					if (type === FeedType.presenter) {
						return feed.presenter === currentFeed.presenter;
					}
					if (type === FeedType.onsiteLocation) {
						return feed.onsiteLocationId === currentFeed.onsiteLocationId;
					}
					return false;
				},
			)
		);

		const streamIds: string[] = [];

		if (type === FeedType.presenter && currentFeed.presenter) {
			streamIds.push(getStreamIdFromGrUser(currentFeed.presenter));
		}
		if (type === FeedType.onsiteLocation && currentFeed.onsiteLocationId) {
			const currLocationStreams = onsiteLocationStreams.get(currentFeed.onsiteLocationId);
			if (currLocationStreams) {
				streamIds.push(...currLocationStreams.map((stream) => stream.id));
			}
		}

		// presenter will always be one, but onsite locations may have several streams
		return streamIds.reduce((acc: { streamId: string, shotResolution: Dimensions }[], curr) => {
			const subscriber = opentok?.subscribers[curr];
			if (subscriber) {
				const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720 : RESOLUTIONS_1080;
				const shotResolution = RESOLUTIONS[determineResolution(
					currentForcedLayout,
					currentShotFeeds[inCurrentShotIndexPres].position,
					currentFeed,
					layoutSize,
				)];

				acc.push({ streamId: curr, shotResolution });
			}
			return acc;
		}, []);
	};

	// Embed: subscribe/resume/pause streams based on current + next shot.
	// Independent of feedScaling — bandwidth optimization must always run.
	useEffect(() => {
		if (!useFeedOptimization || !opentok || !embed) return;
		if (!order && order !== 0) return;

		const needed: NeededFeeds = {
			profiles: new Set<number>(),
			sipStreamIds: new Set<string>(),
			onsiteLocationIds: new Set<number>(),
			screenshareEmails: new Set<string>(),
		};

		const nextShot: Shot | undefined = shots.find(
			(shot: Shot) => shot.shot_data.order === order + 1,
		);

		const collectNeeded = (feeds: ShotFeed[]) => {
			feeds.forEach((feed) => {
				if (feed.presenter) needed.profiles.add(feed.presenter);
				if (feed.sipStreamId) needed.sipStreamIds.add(feed.sipStreamId);
				if (feed.onsiteLocationId != null && feed.onsiteLocationId !== 0) {
					needed.onsiteLocationIds.add(feed.onsiteLocationId);
				}
				if (feed.screenshare) needed.screenshareEmails.add(feed.screenshare);
			});
		};

		collectNeeded(shotFeeds);
		if (nextShot) collectNeeded(nextShot.shot_data.shotFeeds);

		const isStreamNeeded = createIsStreamNeeded(needed, connectedUsers);

		// Resume paused subscribers that are now needed, and subscribe to
		// streams that were never subscribed (skipped by the streamAdded gate).
		opentok.streams.forEach((stream) => {
			const { streamId } = stream;
			const existingSubscriber = opentok.subscribers[streamId];

			if (existingSubscriber) {
				if (opentok.pausedSubscribers.has(streamId) && isStreamNeeded(stream)) {
					opentok.resumeStream(streamId, companionMode);
				}
				return;
			}

			if (isStreamNeeded(stream)) {
				opentok.subscribeToStream(stream, companionMode, true);
			}
		});

		// Pause feeds no longer in current/next shot
		connectedPresenterVideos
			.filter((v) => v.profile && !v.isScreenShare && !v.isSipCall)
			.forEach((v) => {
				if (!needed.profiles.has(v.profile!)) {
					opentok.pauseStream(v.id);
				}
			});

		connectedPresenterVideos
			.filter((v) => v.isSipCall)
			.forEach((v) => {
				if (!needed.sipStreamIds.has(v.id)) {
					opentok.pauseStream(v.id);
				}
			});

		connectedPresenterVideos
			.filter((v) => v.isScreenShare)
			.forEach((v) => {
				const user = connectedUsers.find(
					(u) => u.screenShareWebrtcId === v.id,
				);
				if (user && !needed.screenshareEmails.has(user.email)) {
					opentok.pauseStream(v.id);
				}
			});

		onsiteLocationStreams.forEach((streams, locationId) => {
			if (!needed.onsiteLocationIds.has(locationId)) {
				streams.forEach((s) => {
					opentok.pauseStream(s.id);
				});
			}
		});
	}, [
		shots,
		connectedPresenterVideos,
		connectedUsers,
		order,
		shotFeeds,
		opentok,
		onsiteLocationStreams,
		embed,
		companionMode,
		useFeedOptimization,
	]);

	// Non-embed: always keep at least MAX_VISIBLE_FEEDS subscriber
	// videos active (or all if fewer are connected). Current + next
	// shot presenters are mandatory; remaining slots fill from later
	// shots in order. Independent of the feeds drawer. (BDEV-5535)
	useEffect(() => {
		if (!opentok || embed) return;

		// If the flag is off (or was just turned off), restore any feeds
		// this effect previously paused and bail out.
		if (!useFeedOptimization) {
			if (opentok.pausedSubscribers.size > 0) {
				opentok.pausedSubscribers.forEach((id) => {
					opentok.subscribers[id]?.subscribeToVideo(true);
				});
				opentok.pausedSubscribers.clear();
			}
			return;
		}

		const allSubscriberIds = Object.keys(opentok.subscribers);
		if (allSubscriberIds.length === 0) return;

		const mandatoryProfiles = new Set<number>();
		const fillProfiles: number[] = [];

		if (order != null) {
			const nextShot: Shot | undefined = shots.find(
				(s: Shot) => s.shot_data.order === order + 1,
			);

			// Current + next shot presenters are mandatory
			const collectMandatory = (feeds: ShotFeed[]) => {
				feeds.forEach((feed) => {
					if (feed.presenter) mandatoryProfiles.add(feed.presenter);
				});
			};
			collectMandatory(shotFeeds);
			if (nextShot) collectMandatory(nextShot.shot_data.shotFeeds);

			// Walk remaining shots (order >= current + 2) to fill extra slots
			const laterShots = [...shots]
				.filter((s: Shot) => s.shot_data.order >= order + 2)
				.sort((a: Shot, b: Shot) => a.shot_data.order - b.shot_data.order);

			const seen = new Set<number>(mandatoryProfiles);
			for (const shot of laterShots) {
				for (const feed of shot.shot_data.shotFeeds) {
					if (feed.presenter && !seen.has(feed.presenter)) {
						seen.add(feed.presenter);
						fillProfiles.push(feed.presenter);
					}
				}
				if (seen.size >= MAX_VISIBLE_FEEDS) break;
			}
		}

		// Build a lookup from profile → stream ids. A profile may
		// temporarily have two streams during a reconnect overlap, so
		// collect all IDs to avoid dropping the stale subscriber from
		// the cameraPresenterIds filter.
		const profileToStreamIds = new Map<number, string[]>();
		connectedPresenterVideos.forEach((v) => {
			if (v.profile && !v.isScreenShare && !v.isSipCall) {
				const ids = profileToStreamIds.get(v.profile) ?? [];
				ids.push(v.id);
				profileToStreamIds.set(v.profile, ids);
			}
		});

		// Mandatory presenters (current + next shot) always get video
		const visible = new Set<string>();
		for (const profile of mandatoryProfiles) {
			const streamIds = profileToStreamIds.get(profile);
			if (streamIds) streamIds.forEach((id) => visible.add(id));
		}

		// Fill remaining slots from later shots
		if (visible.size < MAX_VISIBLE_FEEDS) {
			for (const profile of fillProfiles) {
				const streamIds = profileToStreamIds.get(profile);
				if (streamIds) streamIds.forEach((id) => visible.add(id));
				if (visible.size >= MAX_VISIBLE_FEEDS) break;
			}
		}

		// Fill any leftover slots with other connected presenters
		if (visible.size < MAX_VISIBLE_FEEDS) {
			for (const [, streamIds] of profileToStreamIds) {
				for (const id of streamIds) {
					if (!visible.has(id)) visible.add(id);
					if (visible.size >= MAX_VISIBLE_FEEDS) break;
				}
				if (visible.size >= MAX_VISIBLE_FEEDS) break;
			}
		}

		// Only toggle video for camera-presenter subscribers. Screenshare,
		// SIP, and onsite feeds are always visible in the main studio view
		// and must not be touched by this optimisation.
		const cameraPresenterIds = new Set<string>();
		for (const ids of profileToStreamIds.values()) {
			ids.forEach((id) => cameraPresenterIds.add(id));
		}

		allSubscriberIds.forEach((id) => {
			if (!cameraPresenterIds.has(id)) return;

			const isVisible = visible.has(id);
			const alreadyPaused = opentok.pausedSubscribers.has(id);

			// Skip if subscriber is already in the desired state to avoid
			// redundant SDK renegotiation on unrelated dependency changes.
			if (isVisible && !alreadyPaused) return;
			if (!isVisible && alreadyPaused) return;

			opentok.subscribers[id]?.subscribeToVideo(isVisible);
			// Track video-off state so the feeds-drawer thumbnail can show a
			// placeholder instead of a black frame. Audio is intentionally left
			// enabled — only video is toggled for the non-embed optimisation.
			if (isVisible) {
				opentok.pausedSubscribers.delete(id);
			} else {
				opentok.pausedSubscribers.add(id);
			}
		});
	}, [
		useFeedOptimization,
		opentok,
		embed,
		connectedPresenterVideos,
		shots,
		order,
		shotFeeds,
	]);

	// Embed: set layout-aware resolution for subscribed current/next shot feeds.
	// Gated by feedScaling since resolution management is that feature's concern.
	useEffect(() => {
		if (!feedScaling || !opentok || !embed) return;
		if (!order && order !== 0) return;

		const nextShot: Shot | undefined = shots.find(
			(shot: Shot) => shot.shot_data.order === order + 1,
		);

		const thisAndNextShotFeeds: {
			[key: string]: {
				currentShotResolution?: Dimensions,
				nextShotResolution?: Dimensions,
			},
		} = {};

		shotFeeds.forEach((feedCurrent) => {
			const resolutions = getResolutionsForShotFeeds(
				shotFeeds, feedCurrent, forcedLayout,
			);
			resolutions.forEach(({ streamId, shotResolution }) => {
				thisAndNextShotFeeds[streamId] = {
					currentShotResolution: shotResolution,
				};
			});
		});

		if (nextShot) {
			const {
				forcedLayout: nForcedLayout,
				shotFeeds: nShotFeeds,
			} = nextShot.shot_data;
			nShotFeeds
				.filter((feed: ShotFeed) => feed.presenter || feed.onsiteLocationId)
				.forEach((feedNext: ShotFeed) => {
					const resolutions = getResolutionsForShotFeeds(
						nShotFeeds, feedNext, nForcedLayout,
					);
					resolutions.forEach(({ streamId, shotResolution }) => {
						thisAndNextShotFeeds[streamId] = {
							...thisAndNextShotFeeds[streamId],
							nextShotResolution: shotResolution,
						};
					});
				});
		}

		const changedPreferredResolutions: { [key: string]: Dimensions } = {};

		Object.entries(thisAndNextShotFeeds).forEach(
			([key, data]: [string, any]) => {
				const currentShotHeight = data.currentShotResolution?.height || 0;
				const nextShotHeight = data.nextShotResolution?.height || 0;
				const preferredResolution = currentShotHeight > nextShotHeight
					? data.currentShotResolution
					: data.nextShotResolution;

				const subscriber = opentok?.subscribers[key];
				if (
					subscriber
					&& preferredResolution
					&& preferredResolutionChanged(key, preferredResolution)
				) {
					changedPreferredResolutions[key] = preferredResolution;
					subscriber.setPreferredResolution(preferredResolution);
				}
			},
		);

		if (Object.entries(changedPreferredResolutions).length > 0) {
			setPreferredResolutions({
				...preferredResolutions,
				...changedPreferredResolutions,
			});
		}
	}, [
		feedScaling,
		shots,
		connectedPresenterVideos,
		order,
		forcedLayout,
		shotFeeds,
		opentok,
		onsiteLocationStreams,
		embed,
	]);

	useEffect(() => {
		if (
			feedScaling
			&& opentok
			&& !embed
			&& (connectedPresenterVideos.length > 0 || onsiteLocationStreams.size > 0)
		) {
			// update resolution for presenters in the next shot/previous shot
			if (!order && order !== 0) {
				return;
			}

			const thisAndNextShotFeeds: {
				[key: string]: { currentShotResolution?: Dimensions, nextShotResolution?: Dimensions },
			} = {};

			// get number of feeds for current and next shot
			const feedsInCurrentShot = shotFeeds.map((feed) => feed);
			feedsInCurrentShot?.forEach((feedCurrent) => {
				const resolutions = getResolutionsForShotFeeds(shotFeeds, feedCurrent, forcedLayout);
				resolutions.forEach(({ streamId, shotResolution }) => {
					thisAndNextShotFeeds[streamId] = { currentShotResolution: shotResolution };
				});
			});

			const newNextShot: Shot | undefined = shots.find(
				(shot: Shot) => shot.shot_data.order === order + 1,
			);
			if (newNextShot) {
				const { forcedLayout: nForcedLayout, shotFeeds: nShotFeeds } = newNextShot.shot_data;
				const feedsInNextShot = nShotFeeds
					.reduce((accumulator: ShotFeed[], currentFeed: ShotFeed) => {
						if (currentFeed.presenter || currentFeed.onsiteLocationId) {
							accumulator.push(currentFeed);
						}
						return accumulator;
					}, []);
				feedsInNextShot?.forEach((feedNext) => {
					const resolutions = getResolutionsForShotFeeds(nShotFeeds, feedNext, nForcedLayout);
					resolutions.forEach(({ streamId, shotResolution }) => {
						thisAndNextShotFeeds[streamId] = {
							...thisAndNextShotFeeds[streamId],
							nextShotResolution: shotResolution,
						};
					});
				});
			}
			const doNotSetAsLowestResolution = Object.keys(thisAndNextShotFeeds);
			const presenterStreamIds = connectedPresenterVideos.map((v) => v.id);
			const locationStreamIds = [...onsiteLocationStreams.values()]
				.reduce((acc: string[], curr) => [...acc, ...curr.map((v) => v.id)], []);
			const allSubscriberKeys = Object.keys(opentok?.subscribers).filter((key) => (
				presenterStreamIds.includes(key) || locationStreamIds.includes(key)
			));
			const keysToSetAsLowestResolution = allSubscriberKeys.filter(
				(el) => !doNotSetAsLowestResolution.includes(el),
			);
			const changedPreferredResolutions: { [key: string]: Dimensions } = {};

			Object.entries(thisAndNextShotFeeds).forEach(([key, presenter]:[string, any]) => {
				const currentShotHeight = presenter.currentShotResolution?.height || 0;
				const nextShotHeight = presenter.nextShotResolution?.height || 0;
				const preferredResolution = currentShotHeight > nextShotHeight
					? presenter.currentShotResolution
					: presenter.nextShotResolution;

				const subscriber = opentok?.subscribers[key];
				if (preferredResolutionChanged(key, preferredResolution)) {
					changedPreferredResolutions[key] = preferredResolution;
					subscriber?.setPreferredResolution(preferredResolution);
				}
			});
			keysToSetAsLowestResolution.forEach((key) => {
				const subscriber = opentok?.subscribers[key];
				const preferredResolution = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720.LOW : RESOLUTIONS_1080.LOW;
				if (preferredResolutionChanged(key, preferredResolution)) {
					changedPreferredResolutions[key] = preferredResolution;
					subscriber?.setPreferredResolution(preferredResolution);
				}
			});
			if (Object.entries(changedPreferredResolutions).length > 0) {
				setPreferredResolutions({
					...preferredResolutions,
					...changedPreferredResolutions,
				});
			}
		}
	}, [
		feedScaling,
		shots,
		connectedPresenterVideos,
		order,
		forcedLayout,
		shotFeeds,
		opentok,
		onsiteLocationStreams,
	]);
};

export default useResolutionUpdate;
